// Admin → users list. Lives at /users.
//
// Hits GET /api/admin/users with debounced filters; renders a table with a
// per-row three-dot menu of admin actions. State-changing actions confirm
// in a tiny inline prompt and then call the API + refresh the row.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  toast,
} from "@bloom/ui";
import {
  MoreHorizontal,
  Search,
  ShieldCheck,
  ShieldAlert,
  ChevronRight,
  UserCog,
  Ban,
  CheckCircle2,
  LogOut,
  KeyRound,
  UserCheck,
} from "lucide-react";
import { api, ApiError } from "../api/client.ts";
import { useAuth } from "../auth/auth-context.tsx";

interface AdminUserRow {
  _id: string;
  email: string;
  name: string | null;
  locale: "th" | "en" | null;
  role: string;
  emailVerified: boolean;
  banned: boolean;
  twoFactorEnabled: boolean;
  totalSpendThb: number;
  purchaseCount: number;
  lastSeenAt: string | null;
  createdAt: string;
}

interface UsersList {
  items: AdminUserRow[];
  nextCursor: string | null;
}

const ROLES = ["user", "support", "content_manager", "finance", "admin", "superadmin"] as const;

export function UsersIndex() {
  const { user: me } = useAuth();
  const isSuperadmin = me?.role === "superadmin";

  // Filter state — kept in component state, debounced into the actual query.
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [role, setRole] = useState<string>("");
  const [banned, setBanned] = useState<"" | "true" | "false">("");
  const [verified, setVerified] = useState<"" | "true" | "false">("");
  const [joinedSince, setJoinedSince] = useState<string>("");

  const [items, setItems] = useState<AdminUserRow[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // 250ms debounce on the search box.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (debouncedQ) params.set("q", debouncedQ);
    if (role) params.set("role", role);
    if (banned) params.set("banned", banned);
    if (verified) params.set("verified", verified);
    if (joinedSince) params.set("joinedSince", `${joinedSince}T00:00:00.000Z`);
    return params.toString();
  }, [debouncedQ, role, banned, verified, joinedSince]);

  // Fetch on filter change. Reset cursor + items.
  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setCursor(null);
    void api
      .get<UsersList>(`/api/admin/users${queryString ? `?${queryString}` : ""}`)
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setCursor(data.nextCursor);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(`Couldn't load users: ${(err as Error).message}`);
        setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [queryString]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const sep = queryString ? "&" : "?";
      const data = await api.get<UsersList>(
        `/api/admin/users${queryString ? `?${queryString}` : ""}${sep}cursor=${encodeURIComponent(cursor)}`,
      );
      setItems((prev) => [...(prev ?? []), ...data.items]);
      setCursor(data.nextCursor);
    } catch (err) {
      toast.error(`Load-more failed: ${(err as Error).message}`);
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, loadingMore, queryString]);

  const replaceRow = useCallback((id: string, patch: Partial<AdminUserRow>) => {
    setItems((prev) => (prev ? prev.map((r) => (r._id === id ? { ...r, ...patch } : r)) : prev));
  }, []);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Users</h1>
          <p className="text-sm text-[var(--brand-muted)]">
            Search, ban, promote, or step into any account.
          </p>
        </div>
      </header>

      {/* Filter row */}
      <section className="rounded-2xl border border-[var(--brand-border)] bg-white p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div className="relative lg:col-span-2">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--brand-muted)]"
              aria-hidden
            />
            <Input
              type="search"
              placeholder="Email or name"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={role || "any"} onValueChange={(v) => setRole(v === "any" ? "" : v)}>
            <SelectTrigger>
              <SelectValue placeholder="Any role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any role</SelectItem>
              {ROLES.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={banned || "any"}
            onValueChange={(v) => setBanned(v === "any" ? "" : (v as "true" | "false"))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Banned: any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Banned: any</SelectItem>
              <SelectItem value="true">Banned only</SelectItem>
              <SelectItem value="false">Active only</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={verified || "any"}
            onValueChange={(v) => setVerified(v === "any" ? "" : (v as "true" | "false"))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Verified: any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Verified: any</SelectItem>
              <SelectItem value="true">Verified only</SelectItem>
              <SelectItem value="false">Unverified only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-[var(--brand-muted)]">
            Joined since
            <input
              type="date"
              value={joinedSince}
              onChange={(e) => setJoinedSince(e.target.value)}
              className="h-9 rounded-full border border-[var(--brand-border)] bg-[var(--brand-paper)] px-3 text-sm text-[var(--brand-ink)]"
            />
          </label>
          {(q || role || banned || verified || joinedSince) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setQ("");
                setRole("");
                setBanned("");
                setVerified("");
                setJoinedSince("");
              }}
            >
              Clear filters
            </Button>
          )}
        </div>
      </section>

      {/* Table */}
      <section className="rounded-2xl border border-[var(--brand-border)] bg-white">
        {items === null ? (
          <div className="space-y-2 p-6">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-12 animate-pulse rounded-lg bg-[var(--brand-pink-soft)]/40"
              />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-sm text-[var(--brand-muted)]">
            No users match those filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--brand-border)] text-left text-xs font-semibold uppercase tracking-wide text-[var(--brand-muted)]">
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Spend</th>
                  <th className="px-4 py-3">Last seen</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="w-12 px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <UserRow
                    key={u._id}
                    user={u}
                    isSuperadmin={isSuperadmin}
                    onPatch={(patch) => replaceRow(u._id, patch)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {cursor && (
          <div className="flex justify-center border-t border-[var(--brand-border)] p-3">
            <Button variant="ghost" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? "Loading…" : "Load more"}
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}

function UserRow({
  user,
  isSuperadmin,
  onPatch,
}: {
  user: AdminUserRow;
  isSuperadmin: boolean;
  onPatch: (patch: Partial<AdminUserRow>) => void;
}) {
  const navigate = useNavigate();
  const initials = (user.name ?? user.email).slice(0, 2).toUpperCase();
  const lastSeen = user.lastSeenAt
    ? new Date(user.lastSeenAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "2-digit",
      })
    : "—";
  const joined = new Date(user.createdAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "2-digit",
  });

  const go = () => navigate(`/users/${user._id}`);

  return (
    <tr
      className="cursor-pointer border-b border-[var(--brand-border)] transition-colors hover:bg-[var(--brand-pink-soft)]/20"
      onClick={go}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--brand-pink-soft)] font-mono text-xs font-bold text-[var(--brand-ink)]">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="truncate font-medium text-[var(--brand-ink)]">{user.email}</p>
            {user.name ? (
              <p className="truncate text-xs text-[var(--brand-muted)]">{user.name}</p>
            ) : null}
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <RolePill role={user.role} />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 text-xs">
          {user.emailVerified ? (
            <span
              className="inline-flex items-center gap-1 text-emerald-700"
              title="Email verified"
            >
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden /> verified
            </span>
          ) : (
            <span className="text-[var(--brand-muted)]">unverified</span>
          )}
          {user.banned ? (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-1.5 py-0.5 text-rose-700"
              title="Banned"
            >
              <ShieldAlert className="h-3 w-3" aria-hidden /> banned
            </span>
          ) : null}
        </div>
      </td>
      <td className="px-4 py-3 text-right font-mono">
        {user.totalSpendThb > 0 ? (
          <span className="text-[var(--brand-ink)]">
            ฿{user.totalSpendThb.toLocaleString()}
            <span className="ml-1 text-xs text-[var(--brand-muted)]">({user.purchaseCount})</span>
          </span>
        ) : (
          <span className="text-[var(--brand-muted)]">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-[var(--brand-muted)]">{lastSeen}</td>
      <td className="px-4 py-3 text-xs text-[var(--brand-muted)]">{joined}</td>
      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
        <RowActions user={user} isSuperadmin={isSuperadmin} onPatch={onPatch} />
      </td>
    </tr>
  );
}

function RowActions({
  user,
  isSuperadmin,
  onPatch,
}: {
  user: AdminUserRow;
  isSuperadmin: boolean;
  onPatch: (patch: Partial<AdminUserRow>) => void;
}) {
  const navigate = useNavigate();

  const handleBan = async () => {
    const reason = window.prompt(`Ban ${user.email}? Enter a reason:`);
    if (!reason) return;
    try {
      await api.post(`/api/admin/users/${user._id}/ban`, { reason });
      onPatch({ banned: true });
      toast.success(`${user.email} is banned`);
    } catch (err) {
      const msg = err instanceof ApiError ? String(err.message) : (err as Error).message;
      toast.error(`Ban failed: ${msg}`);
    }
  };

  const handleUnban = async () => {
    try {
      await api.post(`/api/admin/users/${user._id}/unban`);
      onPatch({ banned: false });
      toast.success(`${user.email} is unbanned`);
    } catch (err) {
      toast.error(`Unban failed: ${(err as Error).message}`);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm(`Force log-out ${user.email} on every device?`)) return;
    try {
      await api.post(`/api/admin/users/${user._id}/sessions/revoke-all`);
      toast.success("All sessions revoked");
    } catch (err) {
      toast.error(`Revoke failed: ${(err as Error).message}`);
    }
  };

  const handleSetRole = async () => {
    const next = window.prompt(
      `Current role: ${user.role}. New role (user/support/content_manager/finance/admin/superadmin):`,
      user.role,
    );
    if (!next || next === user.role) return;
    try {
      await api.post(`/api/admin/users/${user._id}/role`, { role: next });
      onPatch({ role: next });
      toast.success(`Role updated to ${next}`);
    } catch (err) {
      toast.error(`Role update failed: ${(err as Error).message}`);
    }
  };

  const handleImpersonate = async () => {
    if (
      !window.confirm(
        `Take over ${user.email}'s session? Your admin session is suspended until you stop impersonating.`,
      )
    )
      return;
    try {
      const res = await api.post<{ redirectUrl: string }>(
        `/api/admin/users/${user._id}/impersonate`,
      );
      window.open(res.redirectUrl, "_blank", "noopener");
      toast.success(`Impersonating ${user.email} in a new tab`);
    } catch (err) {
      toast.error(`Impersonate failed: ${(err as Error).message}`);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="rounded-full p-2 text-[var(--brand-muted)] transition-colors hover:bg-[var(--brand-pink-soft)] hover:text-[var(--brand-ink)]"
          aria-label="User actions"
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => navigate(`/users/${user._id}`)}>
          <ChevronRight className="h-4 w-4" aria-hidden /> View detail
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {user.banned ? (
          <DropdownMenuItem onSelect={() => void handleUnban()}>
            <CheckCircle2 className="h-4 w-4 text-emerald-700" aria-hidden /> Unban
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={() => void handleBan()}>
            <Ban className="h-4 w-4 text-rose-700" aria-hidden /> Ban…
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => void handleRevoke()}>
          <LogOut className="h-4 w-4" aria-hidden /> Force log-out
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void handleSetRole()}>
          <UserCog className="h-4 w-4" aria-hidden /> Set role…
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            toast.info("Force-password-reset is wired to revoke-sessions; user must re-login.")
          }
        >
          <KeyRound className="h-4 w-4" aria-hidden /> Force password reset
        </DropdownMenuItem>
        {isSuperadmin ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void handleImpersonate()}>
              <UserCheck className="h-4 w-4 text-[var(--brand-rose)]" aria-hidden /> Impersonate
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RolePill({ role }: { role: string }) {
  const palette: Record<string, string> = {
    user: "bg-slate-100 text-slate-700",
    support: "bg-sky-100 text-sky-800",
    content_manager: "bg-violet-100 text-violet-800",
    finance: "bg-amber-100 text-amber-800",
    admin: "bg-rose-100 text-rose-800",
    superadmin: "bg-[var(--brand-ink)] text-white",
  };
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2 py-0.5 text-xs font-semibold",
        palette[role] ?? palette.user,
      )}
    >
      {role}
    </span>
  );
}

export type { AdminUserRow };
